import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CalendarPlus, FloppyDisk, Trash } from "@phosphor-icons/react";

export const CustomHolidaysCard = () => {
  const { user, setUser } = useAuth();
  const [list, setList] = useState(user?.custom_holidays || []);
  const [draft, setDraft] = useState({ date: "", name: "" });
  const [saving, setSaving] = useState(false);

  const add = () => {
    if (!draft.date) return toast.error("Pilih tanggal dulu");
    if (list.some(h => h.date === draft.date)) return toast.error("Tanggal sudah ada");
    setList([...list, { date: draft.date, name: draft.name.trim() }].sort((a, b) => a.date.localeCompare(b.date)));
    setDraft({ date: "", name: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { custom_holidays: list });
      setUser(data);
      setList(data.custom_holidays || []);
      toast.success("Tanggal libur tersimpan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nb-card p-6 mt-6 space-y-4" data-testid="custom-holidays-card">
      <div>
        <span className="nb-badge bg-[#FFD166]">Hari Libur Manual</span>
        <h2 className="font-display font-bold text-lg mt-2">Cuti bersama / libur RS</h2>
        <p className="text-xs text-[#4A4A4A] mt-1">
          Aturan "Hari Libur" otomatis aktif pada hari Minggu, tanggal merah nasional, dan tanggal-tanggal di bawah ini.
        </p>
      </div>
      <div className="grid sm:grid-cols-12 gap-2 items-end">
        <div className="sm:col-span-4">
          <label className="nb-label">Tanggal</label>
          <input type="date" className="nb-input" value={draft.date} onChange={e => setDraft({ ...draft, date: e.target.value })} data-testid="custom-holiday-date-input" />
        </div>
        <div className="sm:col-span-5">
          <label className="nb-label">Keterangan</label>
          <input className="nb-input" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Cuti Bersama Idul Fitri" data-testid="custom-holiday-name-input" />
        </div>
        <button type="button" onClick={add} className="nb-btn sm:col-span-3" data-testid="custom-holiday-add-button">
          <CalendarPlus size={16} weight="bold" /> Tambah
        </button>
      </div>
      {list.length === 0 ? (
        <p className="text-sm italic text-[#4A4A4A]" data-testid="custom-holidays-empty">Belum ada tanggal libur manual.</p>
      ) : (
        <ul className="space-y-2" data-testid="custom-holidays-list">
          {list.map((h, i) => (
            <li key={h.date} className="flex items-center justify-between border-2 border-[#121212] rounded-lg px-3 py-2 bg-white" data-testid={`custom-holiday-item-${i}`}>
              <div>
                <div className="font-bold text-sm">{new Date(h.date + "T00:00:00").toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
                <div className="text-xs text-[#4A4A4A]">{h.name || "Libur Khusus"}</div>
              </div>
              <button type="button" onClick={() => setList(list.filter(x => x.date !== h.date))} className="nb-btn !py-1 !px-2" data-testid={`custom-holiday-remove-${i}`}>
                <Trash size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {JSON.stringify(list) !== JSON.stringify(user?.custom_holidays || []) && (
        <p className="text-xs font-bold text-[#B45309]" data-testid="custom-holidays-unsaved">Ada perubahan yang belum disimpan.</p>
      )}
      <button type="button" onClick={save} disabled={saving} className="nb-btn nb-btn-primary" data-testid="custom-holidays-save-button">
        <FloppyDisk size={18} weight="bold" /> {saving ? "Menyimpan…" : "Simpan Tanggal Libur"}
      </button>
    </div>
  );
};
